// ============================================================
// Section Pays (Statistiques, NationsGlory) : fonctions pures, sans I/O ni store
// ============================================================
// Données du satellite 1.0.29 : base de NGCountryAPI du groupe du satellite (Nova ou Iris), pays fondés sans
// Wilderness, SafeZone ni WarZone. Chaque groupe a son satellite : « Les deux » interroge les deux et les compare.

import type { CountriesResponse, CountryRowDTO } from '@/services/api.service';

// ==================== Groupes ====================

/** Couleurs de NationsGlory (Nova orange, Iris violet), quand le CICD n'en donne pas au groupe. */
const GROUP_COLORS: Record<string, string> = {
  nova: '#FF6900',
  iris: '#BB00CE',
};

/** Couleur d'un groupe : celle du CICD, sinon celle de NationsGlory, sinon le gris des « autres ». */
export function groupColor(key: string, color?: string): string {
  if (color && color.trim()) return color;
  return GROUP_COLORS[key.toLowerCase()] ?? 'var(--job-other)';
}

/** Une réponse de satellite et le groupe qui l'a servie (clé vide : jeu sans groupes). */
export interface CountryPart {
  key: string;
  label: string;
  color: string;
  data: CountriesResponse;
}

/** Ligne du tableau, avec son groupe (comparaison Nova et Iris). */
export type CountryTableRow = CountryRowDTO & { group: string; groupLabel: string; groupColor: string };

/** Lignes de tous les groupes, chacune marquée de son groupe. */
export function tableRows(parts: readonly CountryPart[]): CountryTableRow[] {
  return parts.flatMap((p) => p.data.table.map((r) => ({ ...r, group: p.key, groupLabel: p.label, groupColor: p.color })));
}

/** Somme d'un chiffre sur tous les groupes (0 quand un groupe n'a pas le bloc). */
export function total(parts: readonly CountryPart[], pick: (d: CountriesResponse) => number | undefined): number {
  return parts.reduce((s, p) => s + (pick(p.data) ?? 0), 0);
}

/** « Nova 12, Iris 30 » : la valeur de chaque groupe, pour les sous-titres des cartes en comparaison. */
export function perGroup(parts: readonly CountryPart[], pick: (d: CountriesResponse) => number | undefined,
  format: (n: number) => string): string {
  return parts.map((p) => `${p.label} ${format(pick(p.data) ?? 0)}`).join(', ');
}

/**
 * Dates de plusieurs séries par jour réunies et triées : chaque groupe a son propre relevé, un jour absent chez l'un
 * compte pour zéro.
 */
export function unionDates(series: readonly (readonly { date: string }[])[]): string[] {
  const set = new Set<string>();
  for (const s of series) for (const d of s) set.add(d.date);
  return [...set].sort();
}

// ==================== R&D ====================

/** Branches de la R&D de NationsGlory, dans l'ordre de NGCountryAPI (la couleur suit la branche). */
export const RD_BRANCH_ORDER = ['global', 'military', 'resources', 'industry', 'technology'] as const;

const RD_BRANCH_LABELS: Record<string, string> = {
  global: 'Global',
  military: 'Militaire',
  resources: 'Ressources',
  industry: 'Industrie',
  technology: 'Technologie',
};

export function rdBranchLabel(key: string): string {
  return RD_BRANCH_LABELS[key] ?? key;
}

export function rdBranchColor(key: string): string {
  const i = (RD_BRANCH_ORDER as readonly string[]).indexOf(key);
  return i >= 0 ? `var(--job-${i + 1})` : 'var(--job-other)';
}

// ==================== Guerres et mondes ====================

const WAR_END_LABELS: Record<string, string> = {
  victory: 'Victoire',
  forfeit: 'Abandon',
  peace: 'Paix signée',
  declined: 'Refusée',
  withdrawn: 'Retirée',
};

export function warEndLabel(key: string): string {
  return WAR_END_LABELS[key] ?? key;
}

/** Nom d'un monde de NationsGlory ; un monde inconnu garde son nom technique. */
export function worldLabel(key: string): string {
  if (key === 'earth') return 'Terre';
  if (key === '?') return 'Monde inconnu';
  return key;
}

// ==================== Tableau ====================

export type CountrySort = 'power' | 'members' | 'claims' | 'rdLevel' | 'bank' | 'gdp' | 'warsWon' | 'createdAt' | 'name';

/** Trie une copie : nombres du plus grand au plus petit (PIB inconnu en dernier), nom de A à Z, création de la plus récente. */
export function sortCountries<T extends CountryRowDTO & { group?: string }>(rows: readonly T[], by: CountrySort): T[] {
  const out = [...rows];
  out.sort((a, b) => {
    let d = 0;
    if (by === 'name') d = (a.name ?? a.id).localeCompare(b.name ?? b.id, 'fr');
    else if (by === 'createdAt') d = (b.createdAt ?? 0) - (a.createdAt ?? 0);
    else if (by === 'gdp') d = (b.gdp ?? -1) - (a.gdp ?? -1);
    else d = (b[by] as number) - (a[by] as number);
    if (d !== 0) return d;
    const g = (a.group ?? '').localeCompare(b.group ?? '');
    return g !== 0 ? g : a.id.localeCompare(b.id);
  });
  return out;
}

/** Filtre par nom du pays ou nom au roster (sans casse ni accents). */
export function filterCountries<T extends CountryRowDTO>(rows: readonly T[], query: string): T[] {
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = norm(query.trim());
  if (!q) return [...rows];
  return rows.filter((r) => norm(r.name ?? '').includes(q) || norm(r.country ?? '').includes(q));
}

// ==================== Erreurs ====================

/**
 * Ce que la page dit d'une réponse en erreur. 404 : le satellite n'a pas encore cette route (version d'avant la
 * 1.0.29). 403 : le grade n'a pas le droit. Sinon le message du satellite.
 */
export function countriesError(status: number | undefined, error: string | undefined): string {
  if (status === 404) return 'Le satellite de ce groupe ne sert pas encore les statistiques des pays : cela arrive avec le satellite 1.0.29.';
  if (status === 403) return "Ton grade n'a pas accès à cette section des statistiques.";
  return error?.trim() || "Les statistiques des pays n'ont pas pu être lues.";
}
